import { z } from 'zod';

import { zISODate } from './common';

/* ─── Запрос ────────────────────────────────────────────────────────────── */

export const dashboardQuerySchema = z.object({
  /** Сколько месяцев показывать на графике. */
  months: z.coerce.number().int().min(3).max(24).default(6),
});
export type DashboardQuery = z.infer<typeof dashboardQuerySchema>;

/* ─── Ответ ─────────────────────────────────────────────────────────────── */

export interface DashboardKpi {
  /** Сумма кошельков, учитываемых в общем балансе. */
  totalBalanceMinor: number;
  /** Изменение остатка за текущий месяц. */
  balanceDeltaMinor: number;

  incomeMinor: number;
  expenseMinor: number;
  netMinor: number;

  /** К выплате в ближайшие 30 дней: людям и по счетам вместе. */
  dueSoonMinor: number;
  overdueMinor: number;
  overdueCount: number;

  currency: string;
  periodLabel: string;
}

export interface CashflowPoint {
  monthKey: string;
  incomeMinor: number;
  expenseMinor: number;
  /** Остаток на конец месяца. */
  balanceMinor: number;
  /** Точка прогноза, а не факт. */
  isForecast: boolean;
}

export interface CategorySlice {
  categoryId: string | null;
  name: string;
  color: string;
  amountMinor: number;
  /** Доля в процентах от всех расходов периода. */
  percent: number;
}

export interface PayrollSummary {
  accruedMinor: number;
  taxMinor: number;
  paidMinor: number;
  remainderMinor: number;
  peopleCount: number;
}

export interface WalletBrief {
  id: string;
  name: string;
  color: string;
  type: string;
  balanceMinor: number;
  currency: string;
  balanceInBaseMinor: number;
}

export interface RecentTransaction {
  id: string;
  direction: 'IN' | 'OUT';
  amountMinor: number;
  currency: string;
  occurredAt: string;
  title: string;
  categoryName: string | null;
  walletName: string;
}

/** Единая строка «ближайших выплат»: и люди, и подписки. */
export interface UpcomingPayment {
  kind: 'PAYOUT' | 'BILL';
  id: string;
  title: string;
  subtitle: string | null;
  amountMinor: number;
  currency: string;
  dueDate: string;
  daysUntilDue: number;
  isOverdue: boolean;
  personId: string | null;
  walletId: string | null;
}

export interface DashboardResponse {
  kpi: DashboardKpi;
  cashflow: CashflowPoint[];
  expensesByCategory: CategorySlice[];
  payroll: PayrollSummary;
  wallets: WalletBrief[];
  upcoming: UpcomingPayment[];
  overdue: UpcomingPayment[];
  recent: RecentTransaction[];
  /** Валюты без курса — их не удалось пересчитать в общий баланс. */
  missingRates: string[];
}

/* ─── Календарь (F-13) ──────────────────────────────────────────────────── */

export const calendarQuerySchema = z.object({
  from: zISODate,
  to: zISODate,
});
export type CalendarQuery = z.infer<typeof calendarQuerySchema>;

export interface CalendarEntry {
  kind: 'PAYOUT' | 'BILL';
  id: string;
  title: string;
  subtitle: string | null;
  amountMinor: number;
  currency: string;
  dueDate: string;
  status: string;
  isOverdue: boolean;
  isPaid: boolean;
  personId: string | null;
}

export interface CalendarResponse {
  entries: CalendarEntry[];
  /** Итоги по дням: ключ — дата, значение — сумма к выплате. */
  dailyTotals: Record<string, number>;
  currency: string;
}

export const moveCalendarEntrySchema = z.object({
  kind: z.enum(['PAYOUT', 'BILL']),
  id: z.string().min(1),
  dueDate: zISODate,
});
export type MoveCalendarEntryInput = z.infer<typeof moveCalendarEntrySchema>;
